import { useSkipFutureAlerts } from "@jugl-web/domain-resources/common/hooks/useSkipFutureAlerts";
import { Menu, MenuSections } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { RESOLVE_TICKET_ALERT_ID } from "@jugl-web/utils/storage";
import { DeleteTicketDialog } from "@jugl-web/domain-resources/tickets/components/DeleteTicketDialog";
import { HeaderIconButton } from "@web-src/modules/client-support/components/HeaderIconButton";
import { ResolveTicketDialog } from "@jugl-web/domain-resources/tickets/components/ResolveTicketDialog";
import { FC, useCallback, useMemo, useState } from "react";
import { ReactComponent as ArrowLeftIcon } from "./assets/arrow-left.svg";
import { ReactComponent as BinIcon } from "./assets/bin.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as MoreIcon } from "./assets/more.svg";
import { ReactComponent as ResolveIcon } from "./assets/resolve.svg";

interface TicketSidebarHeaderProps {
  title: string;
  ticketMetadata?: {
    ticketUniqueId: string;
    ticketOwnerName: string;
  };
  onClose: () => void;
  onGoBack?: () => void;
  onResolveTicket?: () => void;
  onDeleteTicket?: () => void;
}

export const TicketSidebarHeader: FC<TicketSidebarHeaderProps> = ({
  title,
  ticketMetadata,
  onClose,
  onGoBack,
  onResolveTicket,
  onDeleteTicket,
}) => {
  const [isResolveDialogOpen, setIsResolveDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { areFutureAlertsSkipped, onSkipFutureAlertsChange } =
    useSkipFutureAlerts();

  const { t } = useTranslations();

  const handleResolveTicketClick = useCallback(() => {
    if (!onResolveTicket) {
      return;
    }

    if (areFutureAlertsSkipped(RESOLVE_TICKET_ALERT_ID)) {
      onResolveTicket();
      return;
    }

    setIsResolveDialogOpen(true);
  }, [areFutureAlertsSkipped, onResolveTicket]);

  const handleResolveTicketConfirm = useCallback(
    ({ skipFutureAlerts }: { skipFutureAlerts: boolean }) => {
      if (skipFutureAlerts) {
        onSkipFutureAlertsChange(RESOLVE_TICKET_ALERT_ID, true);
      }

      onResolveTicket?.();
    },
    [onResolveTicket, onSkipFutureAlertsChange]
  );

  const menuSections = useMemo<MenuSections>(
    () => [
      [
        {
          id: "resolve",
          label: t({
            id: "client-support-page.resolve-ticket",
            defaultMessage: "Resolve Ticket",
          }),
          icon: <ResolveIcon className="shrink-0" />,
          isHidden: !onResolveTicket,
          onSelect: (_, close) => {
            handleResolveTicketClick();
            close();
          },
        },
        {
          id: "delete",
          label: t({
            id: "client-support-page.delete-ticket",
            defaultMessage: "Delete Ticket",
          }),
          icon: <BinIcon className="shrink-0" />,
          labelClassName: "text-red-500",
          isHidden: !onDeleteTicket,
          onSelect: (_, close) => {
            setIsDeleteDialogOpen(true);
            close();
          },
        },
      ],
    ],
    [handleResolveTicketClick, onDeleteTicket, onResolveTicket, t]
  );

  const isMoreMenuVisible = useMemo(
    () =>
      menuSections.some((section) => section.some((item) => !item.isHidden)),
    [menuSections]
  );

  return (
    <>
      <div className="flex h-[60px] w-full shrink-0 items-center justify-between gap-4 border-b border-solid border-dark-100 px-6">
        <div className="flex items-center gap-3 overflow-hidden">
          {onGoBack && (
            <HeaderIconButton icon={<ArrowLeftIcon />} onClick={onGoBack} />
          )}
          <span className="min-w-0 truncate font-secondary text-base leading-[150%] font-medium text-dark">
            {title}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isMoreMenuVisible && (
            <Menu
              placement="bottom"
              autoClose
              hasBackdrop
              backdropStyle="transparent"
              sections={menuSections}
              renderTrigger={({ Trigger, triggerRef, isOpen }) => (
                <Trigger
                  ref={triggerRef}
                  as={HeaderIconButton}
                  isActive={isOpen}
                  icon={<MoreIcon />}
                />
              )}
            />
          )}
          <HeaderIconButton icon={<CloseIcon />} onClick={onClose} />
        </div>
      </div>
      {ticketMetadata && onResolveTicket && (
        <ResolveTicketDialog
          isOpen={isResolveDialogOpen}
          onClose={() => setIsResolveDialogOpen(false)}
          onConfirm={handleResolveTicketConfirm}
          ticketUniqueId={ticketMetadata.ticketUniqueId}
        />
      )}
      {ticketMetadata && onDeleteTicket && (
        <DeleteTicketDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={onDeleteTicket}
          ticketUniqueId={ticketMetadata.ticketUniqueId}
          ticketOwnerName={ticketMetadata.ticketOwnerName}
        />
      )}
    </>
  );
};
